import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/api'

interface Report {
  id: string
  reporterId: string
  reporterUsername: string
  contentType: 'post' | 'comment' | 'message' | 'user' | 'group' | 'event'
  contentId: string
  contentPreview: string
  reason: string
  description?: string
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed'
  priority: 'low' | 'medium' | 'high' | 'critical'
  createdAt: string
  resolvedAt?: string
  resolvedBy?: string
  action?: string
}

export default function ReportsPage() {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'pending' | 'investigating' | 'resolved' | 'all'>('pending')
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)

  useEffect(() => {
    fetchReports()
  }, [filter])

  const fetchReports = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/reports', {
        params: { status: filter === 'all' ? undefined : filter, limit: 50 },
      })
      setReports(response.data.reports || [])
    } catch (error) {
      console.error('Failed to fetch reports:', error)
      // Mock data
      setReports([
        {
          id: '1',
          reporterId: 'user-123',
          reporterUsername: 'concerned_user',
          contentType: 'post',
          contentId: 'post-456',
          contentPreview: 'This post contains inappropriate content that violates community guidelines...',
          reason: 'harassment',
          description: 'This user is bullying others in the comments section.',
          status: 'pending',
          priority: 'high',
          createdAt: '2024-01-15T10:30:00Z',
        },
        {
          id: '2',
          reporterId: 'user-789',
          reporterUsername: 'helpful_member',
          contentType: 'comment',
          contentId: 'comment-012',
          contentPreview: 'A comment that contains hate speech and discrimination...',
          reason: 'hate_speech',
          status: 'investigating',
          priority: 'critical',
          createdAt: '2024-01-15T09:00:00Z',
        },
        {
          id: '3',
          reporterId: 'user-345',
          reporterUsername: 'safe_user',
          contentType: 'message',
          contentId: 'msg-678',
          contentPreview: 'Unwanted direct message with spam content...',
          reason: 'spam',
          status: 'pending',
          priority: 'low',
          createdAt: '2024-01-15T08:00:00Z',
        },
        {
          id: '4',
          reporterId: 'user-901',
          reporterUsername: 'vigilant_user',
          contentType: 'user',
          contentId: 'user-234',
          contentPreview: 'User profile: @suspicious_account',
          reason: 'fake_account',
          description: 'This appears to be a bot or fake account.',
          status: 'resolved',
          priority: 'medium',
          createdAt: '2024-01-14T15:00:00Z',
          resolvedAt: '2024-01-14T16:30:00Z',
          resolvedBy: 'admin',
          action: 'Account suspended',
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (reportId: string, action: 'investigate' | 'resolve' | 'dismiss', notes?: string) => {
    try {
      await api.post(`/admin/reports/${reportId}/action`, { action, notes })
      fetchReports()
      setSelectedReport(null)
    } catch (error) {
      console.error('Failed to perform action:', error)
    }
  }

  const getPriorityColor = (priority: Report['priority']) => {
    const colors = {
      low: 'bg-gray-500/20 text-gray-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      high: 'bg-orange-500/20 text-orange-400',
      critical: 'bg-red-500/20 text-red-400 animate-pulse',
    }
    return colors[priority]
  }

  const getStatusColor = (status: Report['status']) => {
    const colors = {
      pending: 'bg-blue-500/20 text-blue-400',
      investigating: 'bg-yellow-500/20 text-yellow-400',
      resolved: 'bg-green-500/20 text-green-400',
      dismissed: 'bg-gray-500/20 text-gray-400',
    }
    return colors[status]
  }

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      harassment: '🚫 Harassment',
      hate_speech: '⛔ Hate Speech',
      spam: '📧 Spam',
      nudity: '🔞 Nudity',
      violence: '⚠️ Violence',
      fake_account: '👤 Fake Account',
      misinformation: '📰 Misinformation',
      other: '📋 Other',
    }
    return labels[reason] || reason
  }

  const getContentTypeIcon = (type: Report['contentType']) => {
    const icons = {
      post: '📝',
      comment: '💬',
      message: '✉️',
      user: '👤',
      group: '👥',
      event: '📅',
    }
    return icons[type]
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/admin" className="text-gray-400 hover:text-white">
                Admin
              </Link>
              <span className="text-gray-600">/</span>
              <h1 className="text-xl font-bold">User Reports</h1>
            </div>
            <p className="text-gray-400 text-sm">
              {reports.filter((r) => r.status === 'pending').length} pending reports
            </p>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {(['pending', 'investigating', 'resolved', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg transition ${
                filter === f
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {reports.filter((r) => r.status === 'pending').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700">
            <span className="text-6xl mb-4 block">✅</span>
            <h3 className="text-xl font-semibold mb-2">No reports to show</h3>
            <p className="text-gray-400">
              {filter === 'pending' ? 'All reports have been handled.' : 'No reports match this filter.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Reports List */}
            <div className="space-y-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report)}
                  className={`bg-gray-800 rounded-xl p-4 border cursor-pointer transition ${
                    selectedReport?.id === report.id
                      ? 'border-pink-500'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span>{getContentTypeIcon(report.contentType)}</span>
                      <span className="capitalize">{report.contentType}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(report.priority)}`}>
                        {report.priority.toUpperCase()}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(report.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <p className="text-sm text-gray-300 line-clamp-2 mb-2">{report.contentPreview}</p>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Reported by @{report.reporterUsername}</span>
                    <span>{getReasonLabel(report.reason)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Report Detail Panel */}
            {selectedReport ? (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 sticky top-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Report Details</h3>
                  <span className={`px-3 py-1 rounded text-sm ${getStatusColor(selectedReport.status)}`}>
                    {selectedReport.status}
                  </span>
                </div>

                {/* Content Preview */}
                <div className="bg-gray-900 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2 text-sm text-gray-400">
                    <span>{getContentTypeIcon(selectedReport.contentType)}</span>
                    <span className="capitalize">{selectedReport.contentType}</span>
                  </div>
                  <p className="text-sm">{selectedReport.contentPreview}</p>
                </div>

                {/* Report Info */}
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Reason</span>
                    <span>{getReasonLabel(selectedReport.reason)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Priority</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(selectedReport.priority)}`}>
                      {selectedReport.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Reporter</span>
                    <span>@{selectedReport.reporterUsername}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Submitted</span>
                    <span>{new Date(selectedReport.createdAt).toLocaleString()}</span>
                  </div>
                </div>

                {/* Description */}
                {selectedReport.description && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                    <p className="text-sm bg-gray-900 rounded-lg p-3">{selectedReport.description}</p>
                  </div>
                )}

                {/* Resolution Info */}
                {selectedReport.resolvedAt && (
                  <div className="bg-green-500/10 rounded-lg p-3 mb-4 border border-green-500/30">
                    <h4 className="text-sm font-medium text-green-400 mb-2">Resolution</h4>
                    <p className="text-sm text-gray-300">{selectedReport.action}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Resolved by {selectedReport.resolvedBy} on{' '}
                      {new Date(selectedReport.resolvedAt).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Actions */}
                {selectedReport.status === 'pending' || selectedReport.status === 'investigating' ? (
                  <div className="border-t border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Actions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedReport.status === 'pending' && (
                        <button
                          onClick={() => handleAction(selectedReport.id, 'investigate')}
                          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition"
                        >
                          🔍 Investigate
                        </button>
                      )}
                      <button
                        onClick={() => handleAction(selectedReport.id, 'resolve', 'Content removed')}
                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition"
                      >
                        ✓ Resolve
                      </button>
                      <button
                        onClick={() => handleAction(selectedReport.id, 'dismiss')}
                        className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                      >
                        ✕ Dismiss
                      </button>
                    </div>

                    <div className="mt-4">
                      <Link
                        to={`/admin/moderation?content=${selectedReport.contentId}`}
                        className="block text-center bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition"
                      >
                        View in Moderation Queue
                      </Link>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 flex items-center justify-center text-gray-500">
                Select a report to view details
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
